import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    SxProps,
    Theme,
} from "@mui/material";
import React, { ReactNode, useId } from "react";
import { sxClsx } from "utils/styleClsx";

type AcxDisclosureProps = {
    summary: ReactNode;
    children: ReactNode;
    id?: string;
    defaultExpanded?: boolean;
    disabled?: boolean;
    sx?: SxProps<Theme>;
};

const AcxDisclosure = React.forwardRef<HTMLDivElement, AcxDisclosureProps>(
    (
        {
            summary,
            children,
            id,
            defaultExpanded = false,
            disabled = false,
            sx,
        },
        ref,
    ) => {
        const generatedId = useId();
        const disclosureId = id ?? `acx-disclosure-${generatedId}`;

        return (
            <Accordion
                ref={ref}
                disableGutters
                elevation={0}
                defaultExpanded={defaultExpanded}
                disabled={disabled}
                TransitionProps={{ unmountOnExit: true }}
                sx={sxClsx(
                    {
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        overflow: "hidden",
                        "&:before": { display: "none" },
                        "&.Mui-expanded": { margin: 0 },
                    },
                    sx,
                )}
            >
                <AccordionSummary
                    id={`${disclosureId}-header`}
                    aria-controls={`${disclosureId}-content`}
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                        minHeight: 44,
                        px: 1.5,
                        "&.Mui-expanded": { minHeight: 44 },
                        "& .MuiAccordionSummary-content": {
                            my: 1,
                        },
                        "& .MuiAccordionSummary-content.Mui-expanded": {
                            my: 1,
                        },
                    }}
                >
                    {summary}
                </AccordionSummary>
                <AccordionDetails
                    id={`${disclosureId}-content`}
                    sx={{
                        px: 1.5,
                        py: 1.25,
                        borderTop: 1,
                        borderColor: "divider",
                        bgcolor: "action.hover",
                    }}
                >
                    {children}
                </AccordionDetails>
            </Accordion>
        );
    },
);

AcxDisclosure.displayName = "AcxDisclosure";

export default AcxDisclosure;
